import {Card, Checkbox, FormField, Separator, Switch, type FormControl} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Show, Space, Text, VStack} from 'platform/foundation';

import {useWatch, type UseFormReturn} from 'react-hook-form';

import i18n from '@dms/i18n';

import type {CheckoutProfileFormValues} from '../types/CheckoutProfileFormValuesSchema';
import {checkoutProfileOptions} from '../utils/checkoutProfileOptions';

interface Props {
  control: FormControl<CheckoutProfileFormValues>;
  formApi: UseFormReturn<CheckoutProfileFormValues>;
  fieldErrors?: Record<string, string>;
}

export function CheckoutProfileDeposit(props: Props) {
  const deposit = useWatch({control: props.control, name: 'deposit'});
  const methods = useWatch({control: props.control, name: 'payments.methods'}) ?? [];
  const options = checkoutProfileOptions
    .payments()
    .filter((option) => option.value !== 'CASH_ON_DELIVERY');

  const defaultMethodLabel = i18n.t('entity.checkout.labels.defaultDepositMethod');
  const allowMethodLabels = {
    CASH: i18n.t('entity.checkout.labels.allowCash'),
    CARD: i18n.t('entity.checkout.labels.allowCard'),
    BANK_TRANSFER: i18n.t('entity.customerPaymentSettings.labels.allowBankTransfer'),
  };

  return (
    <Show when={deposit?.isAllowed}>
      <VStack>
        <Heading size={4}>{i18n.t('entity.order.labels.deposit')}</Heading>
        <Space vertical={1} />
        <Text size="small" color="secondary">
          {i18n.t('entity.checkout.labels.depositPaymentSettingsDescription')}
        </Text>
        <Space vertical={4} />
        <Box id="checkout-focus-deposit.isMethodChangeAllowed" tabIndex={-1}>
          <FormField
            control={props.control}
            name="deposit.isMethodChangeAllowed"
            errorMessage={props.fieldErrors?.['deposit.isMethodChangeAllowed']}
            type="switch"
            label={i18n.t('entity.checkout.labels.allowDepositPaymentMethodChange')}
          />
        </Box>
        <Space vertical={4} />
        <Box id="checkout-focus-deposit.methods" tabIndex={-1}>
          <VStack spacing={4}>
            {options.map((option) => {
              const isEnabled = methods.some((method) => method.method === option.value);
              const isAllowed = deposit?.methods?.includes(option.value) ?? false;
              const allowMethodLabel = allowMethodLabels[option.value];

              return (
                <Card key={option.value} variant="inlineGrey">
                  <HStack align="center" justify="space-between" spacing={4}>
                    <HStack align="center" spacing={3}>
                      <Switch
                        name="deposit.methods"
                        errorMessage={props.fieldErrors?.['deposit.methods']}
                        aria-label={allowMethodLabel}
                        value={isAllowed}
                        isDisabled={!isEnabled || props.formApi.formState.isSubmitting}
                        onChange={(enabled) => {
                          const current = props.formApi.getValues('deposit.methods') ?? [];
                          props.formApi.setValue(
                            'deposit.methods',
                            enabled
                              ? [...current, option.value]
                              : current.filter((method) => method !== option.value),
                            {shouldDirty: true}
                          );

                          if (!enabled && deposit?.defaultMethod === option.value) {
                            props.formApi.setValue('deposit.defaultMethod', null, {
                              shouldDirty: true,
                            });
                          }

                          void props.formApi.trigger(['deposit', 'documents']);
                        }}
                      />
                      <Heading size={4}>{allowMethodLabel}</Heading>
                    </HStack>
                    <Box id={`checkout-focus-deposit.defaultMethod.${option.value}`} tabIndex={-1}>
                      <Checkbox
                        name="deposit.defaultMethod"
                        errorMessage={props.fieldErrors?.['deposit.defaultMethod']}
                        aria-label={`${defaultMethodLabel}: ${option.label}`}
                        label={defaultMethodLabel}
                        value={deposit?.defaultMethod === option.value}
                        isDisabled={
                          !isEnabled || !isAllowed || props.formApi.formState.isSubmitting
                        }
                        onChange={(enabled) =>
                          props.formApi.setValue(
                            'deposit.defaultMethod',
                            enabled ? option.value : null,
                            {
                              shouldDirty: true,
                            }
                          )
                        }
                      />
                    </Box>
                  </HStack>
                  <Show when={option.value === 'BANK_TRANSFER' && isAllowed}>
                    <Separator />
                    <Grid columns={4} spacing={4}>
                      <GridItem span={4}>
                        <FormField
                          control={props.control}
                          name="deposit.maturityDays"
                          errorMessage={props.fieldErrors?.['deposit.maturityDays']}
                          type="number"
                          isRequired
                          decimalPlaces={0}
                          label={i18n.t('entity.checkout.labels.defaultDepositMaturityDays')}
                          helperText={i18n.t('entity.checkout.labels.depositMaturityDescription')}
                        />
                      </GridItem>
                    </Grid>
                  </Show>
                </Card>
              );
            })}
          </VStack>
        </Box>
      </VStack>
    </Show>
  );
}
